import { loadLessonFromFile, generateLessonId } from '../utils/lessonParser';

// Courses metadata
const courses = [
  {
    id: 1,
    title: 'AI Fundamentals: From Basics to Implementation',
    description: 'A comprehensive course on artificial intelligence fundamentals, machine learning principles, and practical implementations.',
    longDescription: 'Master the core concepts of artificial intelligence in this comprehensive course. You\'ll learn everything from basic AI principles to advanced neural network architectures, with hands-on projects to reinforce your understanding. Perfect for beginners and intermediate learners looking to build a solid foundation in AI.',
    category: 'AI Fundamentals',
    level: 'Beginner to Intermediate',
    duration: '6 weeks',
    instructor: 'Dr. Sarah Chen',
    enrolledCount: 1248,
    rating: 4.8,
    image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    lessonFiles: [
      '/lessons/ai-fundamentals/introduction.nles',
      '/lessons/ai-fundamentals/machine-learning-fundamentals.nles',
      '/lessons/ai-fundamentals/data-preparation.nles',
      '/lessons/ai-fundamentals/supervised-learning.nles',
      '/lessons/ai-fundamentals/unsupervised-learning.nles',
      '/lessons/ai-fundamentals/neural-networks.nles',
      '/lessons/ai-fundamentals/deep-learning.nles',
      '/lessons/ai-fundamentals/natural-language-processing.nles',
      '/lessons/ai-fundamentals/reinforcement-learning.nles',
      '/lessons/ai-fundamentals/computer-vision.nles',
      '/lessons/ai-fundamentals/final-project.nles'
    ]
  },
  {
    id: 2,
    title: 'Prompt Engineering Mastery',
    description: 'Master the art of crafting effective prompts for AI models.',
    longDescription: 'Learn how to communicate effectively with AI systems through well-crafted prompts. This course covers prompt design principles, optimization techniques, and real-world applications across various domains and AI models.',
    category: 'Prompt Engineering',
    level: 'Intermediate',
    duration: '4 weeks',
    instructor: 'Prof. James Miller',
    enrolledCount: 873,
    rating: 4.7,
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    lessonFiles: [
      '/lessons/prompt-engineering/coming-soon.nles',
      // '/lessons/prompt-engineering/prompt-principles.nles',
      // '/lessons/prompt-engineering/few-shot-learning.nles',
      // '/lessons/prompt-engineering/chain-of-thought.nles',
      // '/lessons/prompt-engineering/creative-applications.nles',
      // '/lessons/prompt-engineering/technical-applications.nles',
      // '/lessons/prompt-engineering/optimization.nles',
      // '/lessons/prompt-engineering/capstone-project.nles'
    ]
  },
  // Additional courses can be added here
];

// Cache for storing parsed lesson data
const lessonCache = new Map();

/**
 * Get all available courses with basic information
 * @returns {Promise<Array>} Array of course objects with basic info
 */
export const getAllCourses = async () => {
  return courses.map(course => ({
    id: course.id,
    title: course.title,
    description: course.description,
    category: course.category,
    level: course.level,
    duration: course.duration,
    instructor: course.instructor,
    image: course.image,
    totalLessons: course.lessonFiles.length
  }));
};

/**
 * Check if a file exists by attempting to fetch it
 * @param {string} filePath - Path to the file
 * @returns {Promise<boolean>} Whether the file exists
 */
const fileExists = async (filePath) => {
  try {
    const response = await fetch(filePath, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error(`Error checking if file exists: ${filePath}`, error);
    return false;
  }
};

/**
 * Load lesson data from a .nles file or use placeholder if file not found
 * @param {string} filePath - Path to the lesson file
 * @param {number} courseId - ID of the course
 * @param {number} index - Index of the lesson in the course
 * @returns {Promise<Object>} Lesson data
 */
const getLessonData = async (filePath, courseId, index) => {
  try {
    // Check cache first
    if (lessonCache.has(filePath)) {
      return lessonCache.get(filePath);
    }
    
    // Extract filename for ID generation
    const fileName = filePath.split('/').pop().replace('.nles', '');
    const lessonId = generateLessonId(courseId, fileName);
    
    // Check if file exists
    const exists = await fileExists(filePath);
    
    if (exists) {
      try {
        // Load content from .nles file
        const lesson = await loadLessonFromFile(filePath);
        
        // Create lesson object with metadata and content
        const lessonData = {
          id: lessonId,
          title: lesson.metadata.title,
          duration: lesson.metadata.duration,
          difficulty: lesson.metadata.difficulty,
          category: lesson.metadata.category,
          completed: index < 4, // First 4 are completed (for demo)
          description: lesson.metadata.description || `Learn about ${lesson.metadata.title} in this comprehensive lesson.`,
          content: lesson.content,
          tags: lesson.metadata.tags || []
        };
        
        // Cache the lesson data
        lessonCache.set(filePath, lessonData);
        return lessonData;
      } catch (error) {
        console.error(`Error loading lesson from file ${filePath}:`, error);
        // Fall back to placeholder
      }
    }
    
    // Fallback to placeholder if file doesn't exist or error occurs
    return {
      id: lessonId,
      title: toTitleCase(fileName.replace(/-/g, ' ')),
      duration: `${30 + (index * 5)} mins`,
      completed: index < 4,
      description: `Lesson ${index + 1} of the course.`,
      content: `# ${toTitleCase(fileName.replace(/-/g, ' '))}\n\nThis is placeholder content. The actual lesson file (${filePath}) could not be loaded.`
    };
  } catch (error) {
    console.error(`Error in getLessonData for ${filePath}:`, error);
    throw error;
  }
};

/**
 * Get detailed information about a specific course including lessons
 * @param {number|string} courseId - The ID of the course to retrieve
 * @returns {Promise<Object|null>} The course object or null if not found
 */
export const getCourseById = async (courseId) => {
  const courseIdNum = parseInt(courseId);
  const course = courses.find(c => c.id === courseIdNum);
  
  if (!course) {
    return null;
  }
  
  try {
    const courseCopy = { ...course };
    
    // Load all lessons (either from files or placeholders)
    const lessonPromises = course.lessonFiles.map((filePath, index) => 
      getLessonData(filePath, course.id, index)
    );
    
    const lessons = await Promise.all(lessonPromises);
    
    courseCopy.lessons = lessons;
    courseCopy.totalLessons = lessons.length;
    courseCopy.completedLessons = lessons.filter(lesson => lesson.completed).length;
    courseCopy.progress = Math.round((courseCopy.completedLessons / courseCopy.totalLessons) * 100);
    
    return courseCopy;
  } catch (error) {
    console.error(`Error loading course ${courseId}:`, error);
    return null;
  }
};

/**
 * Get a specific lesson by ID
 * @param {string} lessonId - The ID of the lesson
 * @returns {Promise<Object|null>} The lesson object or null if not found
 */
export const getLessonById = async (lessonId) => {
  try {
    const lessonIdParts = lessonId.split('_');
    if (lessonIdParts.length < 2) {
      return null;
    }
    
    const courseId = parseInt(lessonIdParts[0]);
    const course = await getCourseById(courseId);
    
    if (!course || !course.lessons) {
      return null;
    }
    
    const lesson = course.lessons.find(l => `${l.id}` === lessonId);
    if (!lesson) {
      return null;
    }
    
    return {
      ...lesson,
      courseId,
      courseTitle: course.title,
      nextLessonId: getNextLessonId(course, lesson.id),
      previousLessonId: getPreviousLessonId(course, lesson.id)
    };
  } catch (error) {
    console.error(`Error getting lesson ${lessonId}:`, error);
    return null;
  }
};

/**
 * Get lessons for a specific course
 * @param {number|string} courseId - The course ID
 * @returns {Promise<Array>} Array of lesson objects
 */
export const getLessonsByCourseId = async (courseId) => {
  const course = await getCourseById(courseId);
  return course ? course.lessons : [];
};

/**
 * Helper function to get the next lesson ID
 */
const getNextLessonId = (course, currentLessonId) => {
  const currentIndex = course.lessons.findIndex(l => `${l.id}` === `${currentLessonId}`);
  if (currentIndex === -1 || currentIndex === course.lessons.length - 1) {
    return null;
  }
  return course.lessons[currentIndex + 1].id;
};

/**
 * Helper function to get the previous lesson ID
 */
const getPreviousLessonId = (course, currentLessonId) => {
  const currentIndex = course.lessons.findIndex(l => `${l.id}` === `${currentLessonId}`);
  if (currentIndex === -1 || currentIndex === 0) {
    return null;
  }
  return course.lessons[currentIndex - 1].id;
};

/**
 * Helper function to convert kebab-case to Title Case
 */
const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

/**
 * In a real application, this function would dynamically load lessons from .nles files
 * @param {Object} course - The course object
 * @returns {Promise<Array>} - Array of lesson objects with content
 */
export const loadLessonsForCourse = async (course) => {
  try {
    const loadedLessons = await Promise.all(
      course.lessonFiles.map(async (filePath) => {
        // This would actually load from the file in a real implementation
        // For demo, we're returning placeholders
        const fileName = filePath.split('/').pop().replace('.nles', '');
        
        // In a real app: const lesson = await loadLessonFromFile(filePath);
        const lessonId = generateLessonId(course.id, fileName);
        
        return {
          id: lessonId,
          title: toTitleCase(fileName.replace(/-/g, ' ')),
          // Other fields would come from the actual .nles file
        };
      })
    );
    
    return loadedLessons;
  } catch (error) {
    console.error(`Error loading lessons for course ${course.id}:`, error);
    return [];
  }
}; 